import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, getFaviconUrl, prefetchFavicons, useIsMobile } from '@heidi/utils';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Source } from '../../contracts';
import {
  buildViewerSourceFromSearchSource,
  getSearchSourceSubtitle,
  openSearchBlockSource,
} from '../answer/openSearchBlockSource';
import { useEvidenceAnswerPorts } from '../answer/ports';

interface InlineSearchBlockProps {
  query: string;
  sources: Source[];
  status: 'searching' | 'completed';
  /** While false, a still-``searching`` row renders frozen instead of spinning (default true). */
  isStreaming?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  /** Position in consecutive search group: 'first' | 'middle' | 'last' | 'only' */
  groupPosition?: 'first' | 'middle' | 'last' | 'only';
  /**
   * EVI-1486 analytics context. Threaded from `ChatTimeline` so each click on
   * a source row emits a fully-joinable `evidence_source_clicked` event.
   */
  chatId?: string;
  messageId?: string;
  sessionId?: string | null;
}

/**
 * Single source item in expanded list.
 *
 * Routes user-library sources (`source.type === 'user_document'`) into the
 * in-app `documentPreviewAtom` instead of `window.open(source.url)` — for
 * library docs the URL is an in-app session deep link (e.g. it ends up
 * navigating to `/scribe/session/<id>#reviewMember=...`) rather than a
 * file URL, so the previous unconditional `window.open` would yank the
 * user out of the chat. Web/integration-partner sources open in the in-app
 * source viewer when the URL requires authenticated fetch or inline HTML
 * rendering (e.g. MIMS proxy paths). Either path emits the unified EVI-1486
 * analytics event so we can attribute every click to its surface and sub-agent query.
 */
const SourceItem = ({
  source,
  citationNumber,
  query,
  chatId,
  messageId,
  sessionId,
}: {
  source: Source;
  citationNumber: number;
  query: string;
  chatId?: string;
  messageId?: string;
  sessionId?: string | null;
}) => {
  const {
    openInViewer,
    openLibraryDocument,
    openInNewTab,
    trackSourceClick,
    isAuthenticatedUrl,
  } = useEvidenceAnswerPorts();
  const isMobile = useIsMobile();

  const handleClick = () => {
    openSearchBlockSource(
      {
        source,
        isMobile,
        isAuthenticatedUrl,
        citationNumber,
        surfaceBlock: 'inline_search_block',
        searchBlockQuery: query,
        chatId,
        messageId,
        sessionId,
      },
      {
        trackSourceClick,
        openLibraryDocument,
        openInViewer: (src) => openInViewer(buildViewerSourceFromSearchSource(src)),
        openInNewTab,
      }
    );
  };

  const subtitle = getSearchSourceSubtitle(source);
  const title = source.title || subtitle;
  const showSubtitle = Boolean(source.title && subtitle && title !== subtitle);

  return (
    <div
      className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-surface-hover"
      onClick={handleClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <img
        src={getFaviconUrl(source.url)}
        alt=""
        className="size-4 shrink-0 rounded-sm"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
      <span className="w-0 flex-1 truncate text-sm text-text-primary">{title}</span>
      {showSubtitle && (
        <TypographyCaption className="min-w-0 truncate text-text-tertiary">
          {subtitle}
        </TypographyCaption>
      )}
    </div>
  );
};

/**
 * V2 Inline Search Block - Collapsible search results for block-based timeline
 *
 * Visual states:
 * - SEARCHING: Pulsing Heidi logo with shimmer text query
 * - COLLAPSED: Search icon with query and "N sources" right-aligned with chevron
 * - EXPANDED: Shows full list of sources (inset)
 *
 * Supports consecutive grouping via groupPosition prop for shared borders
 */
export const InlineSearchBlock = ({
  query,
  sources,
  status,
  isStreaming = true,
  defaultExpanded = false,
  className,
  groupPosition = 'only',
  chatId,
  messageId,
  sessionId,
}: InlineSearchBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Prefetch favicons when sources arrive to prevent flicker
  useEffect(() => {
    if (sources.length > 0) {
      prefetchFavicons(sources.map((s) => s.url));
    }
  }, [sources]);

  const isSearching = status === 'searching';
  const sourceCount = sources.length;

  // Wrapper always provides outer border/radius now
  // Individual items only need dashed separators for middle/last positions
  const borderRadiusClass = {
    only: '',
    first: '',
    middle: '',
    last: '',
  }[groupPosition];

  // No borders between rows - wrapper handles outer border only
  const borderClass = {
    only: '',
    first: '',
    middle: '',
    last: '',
  }[groupPosition];

  // Keyframes for animations
  const animationKeyframes = `
    @keyframes icon-fade-in {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes text-shimmer {
      0% { mask-position: 150%; }
      100% { mask-position: -50%; }
    }
  `;

  // Live search: spinner + shimmer (a still-searching row freezes below once the stream ends).
  if (isSearching && isStreaming) {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden duration-300 animate-in fade-in',
          borderRadiusClass,
          borderClass,
          className
        )}
      >
        <style>{animationKeyframes}</style>
        <div className="flex min-w-0 items-center overflow-hidden p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            {/* Spinning loader */}
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            {/* Text with shimmer effect */}
            <span
              className="w-0 flex-1 truncate text-sm text-text-secondary"
              style={{
                maskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                maskSize: '200% 100%',
                WebkitMaskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                WebkitMaskSize: '200% 100%',
                animation: 'text-shimmer 2s ease-in-out infinite',
              }}
            >
              {query || t('chat.browsingTheWeb')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Interrupted (stream ended mid-search): freeze to a static icon + query, no spinner.
  if (isSearching) {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden duration-300 animate-in fade-in',
          borderRadiusClass,
          borderClass,
          className
        )}
      >
        <div className="flex min-w-0 items-center overflow-hidden p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <Search className="size-4 shrink-0 text-text-tertiary" />
            <span className="w-0 flex-1 truncate text-sm text-text-tertiary">
              {query || t('chat.browsingTheWeb')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no sources and completed (edge case)
  if (sourceCount === 0) {
    return <></>;
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn(
        'w-full min-w-0 overflow-hidden duration-300 animate-in fade-in',
        borderRadiusClass,
        borderClass,
        className
      )}
    >
      <style>{animationKeyframes}</style>

      {/* Header - always visible */}
      <CollapsibleTrigger asChild>
        <button className="flex w-full min-w-0 items-center gap-2 overflow-hidden p-3 text-left transition-colors hover:bg-surface-hover">
          <Search
            className="size-4 shrink-0 text-text-secondary"
            style={{ animation: 'icon-fade-in 250ms ease-out forwards' }}
          />
          <span className="w-0 flex-1 truncate text-sm text-text-secondary">{query}</span>
          <span className="shrink-0 whitespace-nowrap pl-1 text-sm text-text-tertiary duration-200 animate-in fade-in">
            {t(sourceCount === 1 ? 'chat.sourceCount_one' : 'chat.sourceCount_other', {
              count: sourceCount,
            })}
          </span>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-text-tertiary transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
      </CollapsibleTrigger>

      {/* Expanded source list - inset with smooth animation */}
      <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {sourceCount > 0 && (
          <div className="border-t border-border px-3 pb-3 pt-2">
            <div className="space-y-0.5">
              {sources.map((source, idx) => (
                <SourceItem
                  key={`source-${idx}-${source.url}`}
                  source={source}
                  citationNumber={idx + 1}
                  query={query}
                  chatId={chatId}
                  messageId={messageId}
                  sessionId={sessionId}
                />
              ))}
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
